import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Operations Workflow Optimization and Productivity Software | TeamGrid";
const SEO_DESCRIPTION = "Optimize operations with real time workflow visibility, bottleneck detection and SLA tracking to improve throughput and reduce errors.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/operations-execution-teams";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const OperationsAndExecutions = () => {
  const navigate = useNavigate();

  // FAQ data specific to Operations & Executions
  const operationsFAQs = [
    {
      question: "How does TeamGrid optimize operational workflows?",
      answer:
        "TeamGrid uses AI-powered analytics to identify bottlenecks, automate repetitive tasks, and provide real-time insights into your operational processes, helping you streamline workflows and improve efficiency.",
    },
    {
      question: "Can TeamGrid integrate with our existing operational tools?",
      answer:
        "Yes, TeamGrid seamlessly integrates with over 100+ operational tools including project management systems, communication platforms, and monitoring tools to provide a unified view of your operations.",
    },
    {
      question:
        "How quickly can we see improvements in our operational efficiency?",
      answer:
        "Most teams see measurable improvements within the first 2 weeks. Our onboarding process ensures you're set up for success, with typical efficiency gains of 20-30% in the first month.",
    },
    {
      question: "Is TeamGrid suitable for distributed operational teams?",
      answer:
        "Absolutely! TeamGrid is designed specifically for distributed teams, providing real-time visibility, automated reporting, and intelligent coordination across different time zones and locations.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Can TeamGrid identify workflow bottlenecks for operations teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid highlights where work slows or queues, helping operations teams identify workflow bottlenecks." } },
            { "@type": "Question", name: "Does TeamGrid show idle versus productive work time?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid breaks down active work time and waiting or idle periods to help improve throughput and productivity." } },
            { "@type": "Question", name: "Is TeamGrid useful for SLA-driven workflows?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid provides analytics that help teams monitor SLA performance and operational focus across tasks and workflows." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Operations & Execution Teams"
      heroTitle="Operations Workflow <span style='color: #00D1C1; font-weight: 600;'>Optimization & Productivity Software</span>"
      heroSubtitle="Optimize Operational Efficiency with Real-Time Process Visibility"
      primaryButtonText="Get a Demo for Operations Teams"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Improve Throughput & Meet SLAs"
      fullSolutionContentDescription="Deliver consistent results with data-driven operational insights and automated workflow optimization. Our platform helps you identify bottlenecks, streamline processes, and ensure compliance with service level agreements."
      fullSolutionContentOutcomes="Achieve 40% faster throughput, 35% cost reduction, 99.9% SLA compliance, and 50% error reduction across your operational workflows."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={operationsFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for operations and execution management."
      />
    </>
  );
};

export default OperationsAndExecutions;
